// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { apiClient, isSessionDenied, SESSION_PROBE_PATH } from "@/queries/client"

// `/users/self` lives outside `/front-api/*` (ADR-0009): the endpoint is
// reused by the ebiose-core cloud adapter bootstrap, hence its non-UI-specific
// URL. Wire fields are snake_case (PRD §Invariants INV-6).
// QueryKey `["userSelf"]` is owned here. Since DEV-334, API-key mutations
// target their own list key in `queries.apikeys.tsx` instead of invalidating this.
export const getUserSelf = async () => {
  return apiClient.get<User>(SESSION_PROBE_PATH)
}

export const USER_SELF_QUERY_KEY = ["userSelf"]

export const useUserSelf = () => {
  return useQuery<User, Error>({
    queryKey: USER_SELF_QUERY_KEY,
    staleTime: 1000 * 60 * 60,
    // This query is also the session probe. A denial — 401 anonymous, 404
    // account gone — is an answer, so retrying it only delays the login
    // page; any other failure left the question open, and a blip that
    // lasts one round trip must not cost the visitor their session.
    // Backoff is the client default.
    retry: (failureCount, error) => !isSessionDenied(error) && failureCount < 3,
    queryFn: async () => {
      const response = await getUserSelf()
      return response.data
    },
  })
}

// A 200 covers both a fresh `Credited` grant and the idempotent `AlreadyRedeemed`
// no-op — cache is always invalidated so the credit balance refreshes without reload.
export const useRedeemCode = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      apiClient.post("/users/self/redeem", { code }).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userSelf"] })
    },
  })
}

// 409 outcomes share the status code; discriminated by exact response body text
// (PRD §Interfaces) — pinned verbatim so backend and UI stay in lockstep.
const REDEEM_CONFLICT_MESSAGES: Record<string, string> = {
  "This credit code has expired.": "This code has expired.",
  "This credit code has reached its redemption limit.":
    "This code has reached its redemption limit.",
  "You have already activated a welcome coupon.":
    "You've already activated a welcome coupon.",
}

export const getRedeemErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const status = error.response?.status
    if (status === 404) {
      return "Unknown code — please check it and try again."
    }
    if (status === 409) {
      const body =
        typeof error.response?.data === "string" ? error.response.data : ""
      return REDEEM_CONFLICT_MESSAGES[body] ?? "This code could not be redeemed."
    }
  }
  return "Something went wrong — please try again."
}
